/**
 * AppShell - Main Layout Wrapper
 * Combines Header, Sidebar, and content area
 * Includes accessibility features: skip link, ARIA landmarks
 * Integrates keyboard shortcuts and global search
 */

import React, { useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { Box, CssBaseline, useMediaQuery, useTheme } from "@mui/material";
import { Header } from "./Header";
import { Sidebar } from "./Sidebar";
import { KeyboardShortcutsProvider } from "@/components/common/KeyboardShortcuts";
import { GlobalSearch } from "@/components/common/GlobalSearch";
import { ErrorBoundary } from "@/components/common/ErrorBoundary";
import { useNavigationSetup } from "@/hooks/useNavigationSetup";

interface AppShellProps {
  children: React.ReactNode;
}

export const AppShell: React.FC<AppShellProps> = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [searchOpen, setSearchOpen] = useState(false);
  const location = useLocation();

  // Set up navigation utility for non-React code (API interceptors, etc.)
  useNavigationSetup();

  const handleOpenSearch = useCallback(() => setSearchOpen(true), []);
  const handleCloseSearch = useCallback(() => setSearchOpen(false), []);

  return (
    <KeyboardShortcutsProvider onSearch={handleOpenSearch}>
      <Box sx={{ display: "flex", minHeight: "100vh" }}>
        <CssBaseline />

      {/* Skip to main content link for keyboard accessibility */}
      <Box
        component="a"
        href="#main-content"
        sx={{
          position: "absolute",
          left: "-9999px",
          zIndex: 9999,
          padding: 2,
          bgcolor: "primary.main",
          color: "primary.contrastText",
          textDecoration: "none",
          fontWeight: 600,
          "&:focus": {
            left: 0,
            top: 0,
          },
        }}
      >
        Skip to main content
      </Box>

      <Header onOpenSearch={handleOpenSearch} />

      {/* Navigation landmark */}
      <Box component="nav" aria-label="Main navigation">
        <Sidebar />
      </Box>

      {/* Main content area */}
      <Box
        component="main"
        id="main-content"
        tabIndex={-1}
        role="main"
        aria-label="Main content"
        sx={{
          flexGrow: 1,
          // A flex item's min-width is `auto`, i.e. its content — so one wide
          // table made <main> wider than the viewport and the DOCUMENT
          // scrolled sideways, carrying the header and sidebar off screen.
          // With 0, <main> stays viewport-width and the table scrolls inside
          // its own container, which is what TableContainer is for.
          minWidth: 0,
          p: { xs: 2, sm: 3 }, // Responsive padding
          mt: "64px", // Height of AppBar
          bgcolor: "background.default",
          minHeight: "calc(100vh - 64px)",
          // Scroll behavior: on desktop the <main> is its own scroll region so
          // the fixed sidebar stays put while content scrolls. On mobile that
          // nested overflow:auto region is an iOS Safari trap -- when the page's
          // content loads asynchronously after mount, iOS can fail to make it
          // scrollable until a reflow (the "dashboard only scrolls after I
          // navigate away and back" bug). Let the document body scroll natively
          // on mobile instead, which iOS handles reliably. Responsive object =
          // a pure CSS media query (no useMediaQuery first-paint flicker):
          // visible below md, auto at md+ -- matching the isMobile breakpoint.
          overflow: { xs: "visible", md: "auto" },
          // On mobile, don't shift content when sidebar is open (it overlays)
          ml: isMobile ? 0 : undefined,
          "&:focus": {
            outline: "none", // Skip link focuses here, no visible outline needed
          },
        }}
      >
        {/* Content wrapper with max width for readability on wide screens */}
        <Box
          sx={{
            maxWidth: 1400,
            mx: "auto",
            width: "100%",
          }}
        >
          {/* Per-page error boundary: a render crash in one page degrades to
              the fallback INSIDE the shell (Header/Sidebar survive) instead of
              blanking the whole app. Keyed by pathname so it auto-resets on
              navigation. */}
          <ErrorBoundary key={location.pathname}>{children}</ErrorBoundary>
        </Box>
      </Box>

      {/* Live region for screen reader announcements. The global .sr-only
          class (App.tsx), not sx: there a bare `width: 1` is 100%. */}
      <Box
        id="aria-live-region"
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      />

      {/* Global Search Dialog */}
      <GlobalSearch open={searchOpen} onClose={handleCloseSearch} />
    </Box>
    </KeyboardShortcutsProvider>
  );
};
